import React, { useState } from "react";
import { Dialog, DialogFooter } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import {
  MerchantApprovalCreateData,
  createMerchantApproval,
} from "../../services/merchantApproval";
import { showToast } from "../../utils/toast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// label = what the user sees, value = what is sent to the API
const LENDER_OPTIONS = [
  { label: "CAPRION", value: "CAPRION" },
  { label: "Zype", value: "zype" },
  { label: "Cashe", value: "cashe" },
];

const PRESENCE_OPTIONS = [
  { label: "Regional", value: "Regional" },
  { label: "National", value: "National" },
];

const selectClass =
  "w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

const UNIT_MULTIPLIERS: Record<string, number> = {
  l: 100_000,
  lac: 100_000,
  lakh: 100_000,
  lakhs: 100_000,
  cr: 10_000_000,
  crore: 10_000_000,
  crores: 10_000_000,
};

// "5", "2.5L", "1.2 Cr" -> number | null (null = invalid)
const parseAmount = (raw: string): number | null => {
  const str = raw.trim().toLowerCase();
  if (!str) return 0;

  const match = str.match(/^(\d+(?:\.\d+)?)\s*([a-z]+)?$/);
  if (!match) return null;

  const num = parseFloat(match[1]);
  const unit = match[2];

  if (!unit) return num;
  const multiplier = UNIT_MULTIPLIERS[unit];
  return multiplier ? Math.round(num * multiplier) : null;
};

const initialFormData: MerchantApprovalCreateData = {
  name: "",
  legalName: "",
  category: "",
  presence: "",
  centers: 0,
  avgAnnualRevenue: 0,
  nabhApproved: false,
  lender: "",
};

const CreateMerchantApprovalDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [formData, setFormData] =
    useState<MerchantApprovalCreateData>(initialFormData);

  const [centersInput, setCentersInput] = useState("");
  const [revenueInput, setRevenueInput] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedRevenue = parseAmount(revenueInput);

  const handleChange = (
    field: keyof MerchantApprovalCreateData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setCentersInput("");
    setRevenueInput("");
  };

  const handleSubmit = async () => {
    if (
      !formData.name.trim() ||
      !formData.legalName.trim() ||
      !formData.category.trim() ||
      !formData.presence.trim() ||
      !formData.lender.trim()
    ) {
      showToast.error("Please fill all required fields");
      return;
    }

    if (parseAmount(revenueInput) === null) {
      showToast.error("Please enter a valid revenue, e.g. 50L or 2.5Cr");
      return;
    }

    try {
      setIsSubmitting(true);

      await createMerchantApproval(formData);

      showToast.success("Merchant approval added successfully");

      resetForm();

      onClose();
      onCreated();
    } catch (error: any) {
      console.error("Error creating merchant approval:", error);

      showToast.error(error?.message || "Failed to create merchant approval");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;

    resetForm();
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Merchant Approval"
      size="lg"
      preventClose={isSubmitting}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Name */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Enter merchant name"
            />
          </div>

          {/* Legal Name */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Legal Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.legalName}
              onChange={(e) => handleChange("legalName", e.target.value)}
              placeholder="Enter legal name"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Category <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              placeholder="Enter category"
            />
          </div>

          {/* Presence (dropdown) */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Presence <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.presence}
              onChange={(e) => handleChange("presence", e.target.value)}
              className={selectClass}
            >
              <option value="">Select presence</option>
              {PRESENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Centers */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Centers
            </label>
            <Input
              type="text"
              inputMode="numeric"
              value={centersInput}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setCentersInput(digits);
                handleChange("centers", digits === "" ? 0 : Number(digits));
              }}
              placeholder="Enter number of centers"
            />
          </div>

          {/* Average Annual Revenue */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Avg Annual Revenue
            </label>
            <Input
              type="text"
              value={revenueInput}
              onChange={(e) => {
                const value = e.target.value;
                // allow digits, decimal point, and up to 6 letters (L, Cr, lakh, crore...)
                if (!/^[0-9.]*\s?[a-zA-Z]{0,6}$/.test(value)) return;
                setRevenueInput(value);
                handleChange("avgAnnualRevenue", parseAmount(value) ?? 0);
              }}
              placeholder="e.g. 50L, 2.5Cr or 5000000"
            />
            {revenueInput.trim() &&
              (parsedRevenue === null ? (
                <p className="mt-1 text-xs text-red-500">
                  Invalid amount. Use a number with L or Cr, e.g. 50L or 2.5Cr
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  = ₹{parsedRevenue.toLocaleString("en-IN")}
                </p>
              ))}
          </div>

          {/* Lender (dropdown) */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Lender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.lender}
              onChange={(e) => handleChange("lender", e.target.value)}
              className={selectClass}
            >
              <option value="">Select lender</option>
              {LENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* NABH Approved */}
          <div className="flex items-center pt-7">
            <input
              id="nabhApproved"
              type="checkbox"
              checked={formData.nabhApproved}
              onChange={(e) => handleChange("nabhApproved", e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="nabhApproved"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              NABH Approved
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Merchant Approval"}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
};

export default CreateMerchantApprovalDialog;